import React, { useCallback, useMemo, useState } from "react";
import countriesData from "../../../Config/countries.json";
import DateRangePicker from "react-bootstrap-daterangepicker";
import moment from "moment";
import "bootstrap-daterangepicker/daterangepicker.css";
import { Link } from "react-router-dom";
import Select from "react-select";

const VALID_COUNTRY_CODES = new Set(countriesData.countries.map(c => c.value.toLowerCase()));
const countryPrefixes = {};
countriesData.countries.forEach(c => {
    if (c.phoneCode && c.value) {
        countryPrefixes[c.phoneCode] = c.value.toLowerCase();
    }
});
countryPrefixes["1"] = "us";

const SMSSidebar = ({
    balance,
    loadingBalance,
    chatThreads,
    activePhone,
    searchQuery,
    setSearchQuery,
    dateFrom,
    setDateFrom,
    dateTo,
    setDateTo,
    limit,
    setLimit,
    countryFilter,
    setCountryFilter,
    statusFilter,
    setStatusFilter,
    onSelectChat,
    onBillsClick,
    onNewChatClick,
    onSyncClick,
    userCache,
    sidebarOpen,
    setSidebarOpen,
    t
}) => {
    const [showMoreFilters, setShowMoreFilters] = useState(false);

    const countriesOptions = useMemo(() => {
        return [
            { value: "", name: t("Pages.SMS.allCountries") || "جميع الدول", label: t("Pages.SMS.allCountries") || "جميع الدول" },
            ...countriesData.countries
                .filter(c => c.value && c.label)
                .sort((a, b) => a.label > b.label ? 1 : -1)
                .map(item => ({
                    value: item.value.toLowerCase(),
                    name: item.label,
                    label: (
                        <div className="d-flex align-items-center" style={{ gap: '8px' }}>
                            <img 
                                src={process.env.PUBLIC_URL + item.icon} 
                                alt={item.label} 
                                style={{ width: '20px', height: '14px', objectFit: 'cover', borderRadius: '2px' }}
                            />
                            <span>{item.label}</span>
                        </div>
                    )
                }))
        ];
    }, [t]);

    const getCountryFromPhone = useCallback((phone) => {
        if (!phone) return null;
        const cleanPhone = phone.replace(/\D/g, '');
        const prefix = Object.keys(countryPrefixes)
            .sort((a, b) => b.length - a.length)
            .find(pref => cleanPhone.startsWith(pref));
        if (prefix) {
            const countryCode = countryPrefixes[prefix];
            return VALID_COUNTRY_CODES.has(countryCode) ? countryCode : null;
        }
        return null;
    }, []);

    const getCountryFlag = useCallback((countryCode) => {
        if (!countryCode) return "";
        const code = countryCode.toLowerCase();
        const found = countriesData.countries.find(c => c.value === code || c.alpha3?.toLowerCase() === code);
        return found ? found.icon : `/assets/flags/32x32/${code}.png`;
    }, []);

    const getCountryName = useCallback((countryCode) => {
        if (!countryCode) return "";
        const code = countryCode.toLowerCase();
        const found = countriesData.countries.find(c => c.value === code || c.alpha3?.toLowerCase() === code);
        return found ? found.label : countryCode.toUpperCase();
    }, []);

    const formatMessageTime = (timestamp) => {
        if (!timestamp) return "";
        const date = new Date(timestamp * 1000);
        return date.toLocaleDateString(undefined, { month: "short", day: "numeric" }) + " " + date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
    };

    const handleApplyDate = (event, picker) => {
        setDateFrom(picker.startDate.format('YYYY-MM-DD'));
        setDateTo(picker.endDate.format('YYYY-MM-DD'));
    };

    const dateRangeLabel = dateFrom && dateTo 
        ? `${moment(dateFrom).format('DD/MM/YYYY')} - ${moment(dateTo).format('DD/MM/YYYY')}` 
        : t("Pages.SMS.selectDateRange") || "اختر الفترة الزمنية";

    return (
        <div className={`chatbot-sidebar-wrapper ${sidebarOpen ? "panel-open" : "panel-closed"}`}>
            {/* Sidebar Rail */}
            <div className="sidebar-rail d-flex flex-column justify-content-between align-items-center py-3">
                <div className="rail-top d-flex flex-column align-items-center w-100">
                    <a href="/" className="rail-logo-wrapper mb-3 d-block">
                        <img
                            src={sidebarOpen ? "/assets/images/logo/logo.png" : "/assets/images/logo/logosmall.png"}
                            alt={t("alarabeyya")}
                            className="rail-logo"
                            style={{ width: sidebarOpen ? '120px' : '32px', transition: 'width 0.3s ease' }}
                        />
                    </a>
                    
                    {/* Toggle menu */}
                    <button
                        type="button"
                        className="rail-btn hamburger-btn btn btn-link text-decoration-none d-flex align-items-center"
                        onClick={() => setSidebarOpen(!sidebarOpen)}
                        title={sidebarOpen ? t("Pages.Chatbot.CloseMenu") : t("Pages.Chatbot.OpenMenu")}
                    >
                        <i className="fal fa-bars"></i>
                        {sidebarOpen && <span className="rail-btn-text mr-2">{t("Pages.Chatbot.CloseMenu")}</span>}
                    </button>

                    {/* New message compose */}
                    <button
                        type="button"
                        className="rail-btn new-chat-btn btn btn-link text-decoration-none d-flex align-items-center"
                        onClick={onNewChatClick}
                        title={t("Pages.SMS.send") || "رسالة جديدة"}
                    >
                        <i className="fal fa-comment-medical"></i>
                        {sidebarOpen && <span className="rail-btn-text mr-2">{t("Pages.SMS.send") || "رسالة جديدة"}</span>}
                    </button>

                    {/* Sync history */}
                    <button
                        type="button"
                        className="rail-btn btn btn-link text-decoration-none d-flex align-items-center"
                        onClick={onSyncClick}
                        title={t("Pages.SMS.sync") || "مزامنة"}
                    >
                        <i className="fal fa-sync"></i>
                        {sidebarOpen && <span className="rail-btn-text mr-2">{t("Pages.SMS.sync") || "مزامنة"}</span>}
                    </button>

                    {/* Billing transactions */}
                    <button
                        type="button"
                        className="rail-btn btn btn-link text-decoration-none d-flex align-items-center"
                        onClick={onBillsClick}
                        title={t("Pages.SMS.bills") || "فواتير ClickSend"}
                    >
                        <i className="fal fa-file-invoice-dollar"></i>
                        {sidebarOpen && <span className="rail-btn-text mr-2">{t("Pages.SMS.bills") || "فواتير ClickSend"}</span>}
                    </button>

                    {/* Recharge ClickSend Link */}
                    <a
                        href="https://dashboard.clicksend.com/#/billing/recharge"
                        target="_blank"
                        rel="noreferrer"
                        className="rail-btn btn btn-link text-decoration-none d-flex align-items-center"
                        title={t("Pages.SMS.recharge") || "شحن الرصيد"}
                    >
                        <i className="fal fa-credit-card"></i>
                        {sidebarOpen && <span className="rail-btn-text mr-2">{t("Pages.SMS.recharge") || "شحن الرصيد"}</span>}
                    </a>

                    {/* Link to User/Control */}
                    <Link
                        to="/User/Control"
                        className="rail-btn btn btn-link text-decoration-none d-flex align-items-center"
                        title={t("Pages.UserControl.title")}
                    >
                        <i className="fal fa-user-cog"></i>
                        {sidebarOpen && <span className="rail-btn-text mr-2">{t("Pages.UserControl.title")}</span>}
                    </Link>
                </div>
            </div>

            {/* Sidebar detailed panel */}
            {sidebarOpen && (
                <div className="sidebar-panel bg-white border-left h-100 d-flex flex-column" style={{ width: '350px' }}>
                    <div className="panel-tab-content history-tab-content d-flex flex-column h-100">
                        {/* Title */}
                        <div className="panel-header d-flex align-items-center justify-content-between p-3 border-bottom">
                            <h3 className="panel-title h6 font-weight-bold m-0 d-flex align-items-center">
                                <i className="fal fa-sms ml-2 text-success" style={{ fontSize: '18px' }}></i>
                                <span>{t("Pages.SMS.title") || "رسائل SMS"}</span>
                            </h3>
                        </div>

                        {/* Balance Banner */}
                        <div className="sms-balance-banner p-3 d-flex justify-content-between align-items-center" style={{ backgroundColor: '#f1f5f9', borderBottom: '1px solid #e2e8f0' }}>
                            <span className="sms-balance-title text-muted small">{t("Pages.SMS.balance") || "رصيد ClickSend الحالي:"}</span>
                            <span className="sms-balance-amount font-weight-bold text-success">
                                {loadingBalance ? (
                                    <i className="fa-spin fas fa-spinner"></i>
                                ) : (
                                    `$${balance}`
                                )}
                            </span>
                        </div>

                        {/* Search and Filters */}
                        <div className="sms-search-container p-3 border-bottom">
                            {/* Search input */}
                            <div className="sms-search-wrapper mb-3 position-relative" style={{ backgroundColor: '#f1f5f9', borderRadius: '8px', padding: '4px 12px' }}>
                                <i className="fal fa-search sms-search-icon" style={{ color: '#94a3b8' }}></i>
                                <input
                                    type="text"
                                    className="sms-search-input"
                                    placeholder={t("Pages.SMS.searchPlaceholder") || "البحث عن هاتف أو اسم..."}
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    style={{ border: 'none', background: 'transparent', outline: 'none', width: '100%', padding: '6px' }}
                                />
                            </div>

                            {/* Date Picker */}
                            <div className="row g-2 mb-2">
                                <div className="col-12">
                                    <label className="small text-muted mb-1 d-block">{t("Pages.SMS.dateRange") || "الفترة الزمنية"}</label>
                                    <DateRangePicker
                                        initialSettings={{
                                            opens: 'left',
                                            autoUpdateInput: true,
                                            startDate: dateFrom ? moment(dateFrom).toDate() : moment().subtract(29, 'days').toDate(),
                                            endDate: dateTo ? moment(dateTo).toDate() : moment().toDate(),
                                            locale: {
                                                format: 'DD/MM/YYYY',
                                                applyLabel: t('Apply') || 'اعتماد',
                                                cancelLabel: t('Close') || 'إغلاق',
                                                customRangeLabel: t('range.customRange') || 'فترة مخصصة'
                                            }
                                        }}
                                        onApply={handleApplyDate}
                                    >
                                        <input
                                            type="text"
                                            className="sms-date-input form-control form-control-sm text-center clickable"
                                            readOnly
                                            value={dateRangeLabel}
                                            style={{ cursor: 'pointer', borderRadius: '6px' }}
                                        />
                                    </DateRangePicker>
                                </div>
                            </div>

                            {/* More Filters Toggle */}
                            <div className="text-center my-2">
                                <button
                                    type="button"
                                    className="btn btn-sm btn-link text-success text-decoration-none font-weight-bold d-flex align-items-center justify-content-center w-100 gap-1"
                                    onClick={() => setShowMoreFilters(!showMoreFilters)}
                                    style={{ fontSize: '12px', border: 'none', background: 'transparent', outline: 'none' }}
                                >
                                    <i className={`fal ${showMoreFilters ? "fa-chevron-up" : "fa-chevron-down"}`}></i>
                                    <span>{showMoreFilters ? t("Pages.SMS.lessFilters") || "إخفاء خيارات التصفية" : t("Pages.SMS.moreFilters") || "خيارات تصفية إضافية"}</span>
                                </button>
                            </div>

                            {/* Other Select Filters */}
                            {showMoreFilters && (
                                <div className="row g-2 animate__animated animate__fadeIn" style={{ animationDuration: '0.2s' }}>
                                    <div className="col-6">
                                        <label className="small text-muted mb-1 d-block">{t("Pages.SMS.countryFilter") || "الدولة"}</label>
                                        <Select
                                            value={countriesOptions.find(opt => opt.value === (countryFilter || "").toLowerCase()) || countriesOptions[0]}
                                            onChange={(val) => setCountryFilter(val ? val.value : "")}
                                            options={countriesOptions}
                                            placeholder={t("Pages.SMS.countryFilter") || "الدولة"}
                                            isClearable={false}
                                            isRtl={true}
                                            isSearchable={true}
                                            filterOption={(candidate, input) => {
                                                if (input) {
                                                    return (candidate.data.name || "").toLowerCase().includes(input.toLowerCase());
                                                }
                                                return true;
                                            }}
                                            theme={theme => ({
                                                ...theme,
                                                borderRadius: 6,
                                                colors: {
                                                    ...theme.colors,
                                                    primary25: '#00a884',
                                                    primary: '#00a884',
                                                },
                                            })}
                                            styles={{
                                                control: (provided) => ({
                                                    ...provided,
                                                    minHeight: '31px',
                                                    height: '31px',
                                                    fontSize: '12px',
                                                    borderRadius: '6px',
                                                    borderColor: '#ced4da',
                                                    boxShadow: 'none',
                                                    '&:hover': {
                                                        borderColor: '#00a884'
                                                    }
                                                }),
                                                valueContainer: (provided) => ({
                                                    ...provided,
                                                    padding: '0 8px',
                                                    height: '31px',
                                                    display: 'flex',
                                                    alignItems: 'center'
                                                }),
                                                input: (provided) => ({
                                                    ...provided,
                                                    margin: '0px',
                                                    padding: '0px'
                                                }),
                                                singleValue: (provided) => ({
                                                    ...provided,
                                                    margin: '0px'
                                                }),
                                                indicatorsContainer: (provided) => ({
                                                    ...provided,
                                                    height: '29px'
                                                }),
                                                dropdownIndicator: (provided) => ({
                                                    ...provided,
                                                    padding: '4px'
                                                }),
                                                clearIndicator: (provided) => ({
                                                    ...provided,
                                                    padding: '4px'
                                                }),
                                                option: (provided, state) => ({
                                                    ...provided,
                                                    fontSize: '12px',
                                                    padding: '6px 12px',
                                                    backgroundColor: state.isSelected ? '#00a884' : (state.isFocused ? '#f1f5f9' : 'transparent'),
                                                    color: state.isSelected ? '#ffffff' : '#334155',
                                                    '&:active': {
                                                        backgroundColor: '#00a884',
                                                        color: '#ffffff'
                                                    }
                                                })
                                            }}
                                        />
                                    </div>
                                    <div className="col-6">
                                        <label className="small text-muted mb-1 d-block">{t("Pages.SMS.limitFilter") || "عدد الرسائل"}</label>
                                        <select
                                            className="form-control form-control-sm"
                                            value={limit}
                                            onChange={(e) => setLimit(Number(e.target.value))}
                                            style={{ borderRadius: '6px' }}
                                        >
                                            <option value={30}>30</option>
                                            <option value={50}>50</option>
                                            <option value={100}>100</option>
                                            <option value={150}>150</option>
                                            <option value={200}>200</option>
                                        </select>
                                    </div>
                                    <div className="col-12 mt-2">
                                        <label className="small text-muted mb-1 d-block">{t("Pages.SMS.statusFilter") || "حالة الإرسال"}</label>
                                        <select
                                            className="form-control form-control-sm"
                                            value={statusFilter}
                                            onChange={(e) => setStatusFilter(e.target.value)}
                                            style={{ borderRadius: '6px' }}
                                        >
                                            <option value="all">{t("Pages.SMS.allStatuses") || "جميع الحالات"}</option>
                                            <option value="Sent">{t("Pages.SMS.sentStatus") || "تم الإرسال (Sent)"}</option>
                                            <option value="Failed">{t("Pages.SMS.failedStatus") || "فشل الإرسال (Failed)"}</option>
                                            <option value="Delivered">{t("Pages.SMS.deliveredStatus") || "تم التسليم (Delivered)"}</option>
                                        </select>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Chats list */}
                        <div className="panel-scroll-content flex-grow-1 overflow-auto" style={{ overflowY: 'auto' }}>
                            {chatThreads.length > 0 ? (
                                chatThreads.map((thread) => {
                                    const countryCode = getCountryFromPhone(thread.phone);
                                    const flagPath = getCountryFlag(countryCode);
                                    const countryName = getCountryName(countryCode);
                                    const cachedUser = userCache && userCache[thread.phone];
                                    const isSystemUser = cachedUser && !cachedUser.isWhatsAppOnly;
                                    const initials = isSystemUser ? String(cachedUser.firstName || "?").substring(0, 1).toUpperCase() : "";

                                    return (
                                        <div
                                            key={thread.id}
                                            className={`sms-chat-item d-flex align-items-center p-3 border-bottom clickable ${activePhone === thread.phone ? "active" : ""}`}
                                            onClick={() => onSelectChat(thread.phone)}
                                            style={{ cursor: 'pointer', transition: 'background-color 0.2s' }}
                                        >
                                            {/* Photo/Avatar/Flag */}
                                            <div className="sms-avatar ml-2" style={{ width: '45px', height: '45px', borderRadius: '50%', background: '#008069', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', flexShrink: 0 }}>
                                                {isSystemUser && cachedUser.photo ? (
                                                    <img
                                                        src={cachedUser.photo.startsWith("http") ? cachedUser.photo : (process.env.REACT_APP_BASE_URL || "") + (cachedUser.photo.startsWith("/") ? "" : "/") + cachedUser.photo}
                                                        alt={cachedUser.firstName}
                                                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                    />
                                                ) : isSystemUser ? (
                                                    <span className="font-weight-bold">{initials}</span>
                                                ) : flagPath ? (
                                                    <img
                                                        src={process.env.PUBLIC_URL + flagPath}
                                                        alt={countryName}
                                                        title={countryName}
                                                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                        onError={(e) => {
                                                            e.target.style.display = "none";
                                                            e.target.parentNode.innerText = thread.phone.substring(0, 2);
                                                        }}
                                                    />
                                                ) : (
                                                    thread.phone.substring(0, 2)
                                                )}
                                            </div>

                                            {/* Chat text */}
                                            <div className="sms-chat-info overflow-hidden flex-grow-1">
                                                <div className="sms-chat-info-header d-flex justify-content-between align-items-center">
                                                    <h5 className="sms-chat-name m-0 font-weight-bold text-truncate text-dark d-flex align-items-center" dir="ltr" style={{ fontSize: '14px' }}>
                                                        {isSystemUser ? `${cachedUser.firstName} ${cachedUser.lastName}` : thread.phone}
                                                        {!isSystemUser && countryName && (
                                                            <small className="text-muted mr-1" style={{ fontSize: '10px' }}>({countryName})</small>
                                                        )}
                                                    </h5>
                                                    <span className="sms-chat-time text-muted mr-2" style={{ fontSize: '10px', flexShrink: 0 }}>
                                                        {formatMessageTime(thread.latestMessage?.date)}
                                                    </span>
                                                </div>
                                                <div className="sms-chat-message-preview text-muted small text-truncate mt-1 d-flex align-items-center justify-content-between">
                                                    <span className="text-truncate flex-grow-1 text-right">{thread.latestMessage?.body}</span>
                                                    
                                                    <div className="d-flex align-items-center mr-2" style={{ gap: '6px' }}>
                                                        <span className="badge badge-secondary" style={{ fontSize: '10px', backgroundColor: '#e2e8f0', color: '#475569', borderRadius: '4px', padding: '2px 5px', fontWeight: 'normal' }}>
                                                            {thread.totalCount} {t("Pages.SMS.messagesCount") || "رسائل"}
                                                        </span>
                                                        <span className="badge badge-info" style={{ fontSize: '10px', backgroundColor: '#e0f2fe', color: '#0369a1', borderRadius: '4px', padding: '2px 5px', fontWeight: 'bold' }}>
                                                            ${(thread.totalCost || 0).toFixed(4)}
                                                        </span>
                                                        
                                                        {/* Alert indicator if delivery failed */}
                                                        {thread.latestMessage?.status === 'Failed' && (
                                                            <span className="badge badge-danger text-white" style={{ padding: '3px 6px', borderRadius: '4px', fontSize: '9px' }} title={t("Pages.SMS.deliveryFailed") || "فشل إرسال آخر رسالة"}>
                                                                <i className="fas fa-exclamation-triangle"></i>
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })
                            ) : (
                                <div className="text-center py-5 text-muted">
                                    <i className="fal fa-comments display-4 d-block mb-2"></i>
                                    {t("Pages.SMS.noHistory") || "لا يوجد سجل رسائل بعد"}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SMSSidebar;
